import { globals } from 'config/globals';
import { Controller } from 'core/Controller';
import { Events } from 'core/events/Events';
import { AppEvents } from 'app/events/AppEvents';
import { SectionType, DepartmentType, ProjectSlugType, ScrollerType } from 'app/data/Enums';
import { HistoryStateModel } from 'app/model/HistoryStateModel';
import { MouseController } from 'app/chrome/mouse/MouseController';
import { VerticalDragController } from 'app/chrome/verticaldrag/VerticalDragController';
import { ScrollbarController } from 'app/chrome/scrollbar/ScrollbarController';
import { PixiController } from 'app/services/PixiController';
import { WorkController } from 'app/views/work/WorkController';

import { SidePagingClickEvent } from 'app/events/SidePagingClickEvent';
import { SidebarChangeEvent } from 'app/events/SidebarChangeEvent';
import { QueProjectEvent } from 'app/events/QueProjectEvent';
import { UIController } from 'app/views/ui/UIController';
import { device } from 'utils/device';

import { UIThemeChangeEvent } from 'app/events/UIThemeChangeEvent';
import DelayHelper from 'utils/timers';
import SiteTransitions from 'app/services/SiteTransitions';
import { Simple } from 'utils/analytics';

import { ProjectView } from './ProjectView';
import { ProjectMedia } from './ProjectMedia';
import { ProjectScroll } from './ProjectScroll';
import { ProjectLoading } from './ProjectLoading';
import { HeaderType } from './project.types';

/**
 * Project section controller (routed at /project/:projectslug). Orchestrates
 * the per-concern sub-objects in this folder (see project.types.ts for the
 * decomposition pattern). Keeps: the lifecycle hooks, the route entries, the
 * full public surface used by AppController / SiteTransitions /
 * NavMenuController, every Events.on(...) registration, and all
 * cross-controller access (this.get(...) / injected controllers / the
 * globals.activeTile + globals.activeWorkController globals that WorkController
 * sets for the work-to-project zoom flow).
 */
export class ProjectController extends Controller {
	private _active: boolean = false;
	private _isSetup: boolean = false;
	private _DOMStructureSet: boolean = false;
	private _closingProject: boolean = false;

	private _$circleProgess: HTMLElement;

	private _mouseController: MouseController;
	private _scrollbarController: ScrollbarController;
	private _pixiController: PixiController;
	private _spacesController: WorkController;
	private _screensController: WorkController;
	private _systemsController: WorkController;
	private _activeWorkController: WorkController;
	private _paused: boolean = false;

	private _activeDepartment: DepartmentType = DepartmentType.SPACES;
	private _activeSlug: ProjectSlugType;

	private _timeouts: any = {};
	private _state: HistoryStateModel;
	private _oldState: HistoryStateModel;
	private _isDeeplink: boolean;

	private _innerHeight: number;

	private _caseStudyActive = false;

	private _allowResize: boolean = true;
	private _topUITheme: string = 'light';

	//Per-concern sub-objects (see project.types.ts for the pattern):
	private _loading: ProjectLoading;
	private _view: ProjectView;
	private _media: ProjectMedia;
	private _scroll: ProjectScroll;

	constructor(id: string) {
		super(id);

		this._loading = new ProjectLoading();

		this._view = new ProjectView(this.DOM);

		this._media = new ProjectMedia(this.DOM, {
			getScroll: () => this._scrollbarController.getScroll(),
			getInnerHeight: () => this._innerHeight
		});

		this._scroll = new ProjectScroll(this.DOM, {
			isActive: () => this._active,
			isPaused: () => this._paused,
			isDeeplink: () => this._isDeeplink,
			isClosing: () => this._closingProject,
			getActiveSlug: () => this._activeSlug,
			getContainer: () => this.container,
			getCircleProgressEl: () => this._$circleProgess,
			getInnerHeight: () => this._innerHeight,
			getTopUITheme: () => this._topUITheme,
			setUiThemeType: (type: string) => this._setUiThemeType(type),
			getRealProgress: () => this._scrollbarController.getRealProgress(),
			getMaxScroll: () => this._scrollbarController.getMaxScroll(),
			isPixiScrollActive: () => this._scrollbarController.pixiScrollActive,
			isNativeScroller: () => this._scrollbarController.isNativeType,
			getPlaneY: () => this._scrollbarController.planeY,
			scrollActiveTile: (y: number) =>
				globals.activeTile.scroll(y, this._scrollbarController.pixiScrollActive),
			onWebGLPause: () => {
				this._pixiController.pause();
				this._activeWorkController.pauseOnProject();
			},
			onWebGLUnpause: () => {
				this._pixiController.unpause();
				this._activeWorkController.unpause(true);
				this._activeWorkController.unPauseOnProject();
			}
		});
	}

	public init(params: any): void {
		//Set the opening state to the homepage:

		this._active = true;
		this._activeSlug = params ? params.projectslug : 'march-for-giants';

		let departmentTitle;
		if (this._activeDepartment === DepartmentType.SPACES) departmentTitle = `Spaces`;
		else if (this._activeDepartment === DepartmentType.SCREENS) departmentTitle = `Screens`;
		else departmentTitle = `Systems`;

		let openingState: HistoryStateModel = {
			section: SectionType.PROJECT,
			subsection: this._activeDepartment,
			projectSlug: this._activeSlug,
			title: this._activeDepartment === DepartmentType.SPACES ? departmentTitle : departmentTitle,
			href: `/project/${this._activeSlug}`
		};

		Simple.event('deeplink', {
			section: SectionType.PROJECT,
			department: this._activeDepartment,
			slug: this._activeSlug
		});

		Events.dispatch(AppEvents.SET_INTIAL_STATE, openingState);

		//Direct link so DOM structure / content will be there on page load:
		this._DOMStructureSet = true;
		this._isDeeplink = true;

		Events.on(UIThemeChangeEvent, this._onThemeChange.bind(this));
	}

	/** Screens route */
	public screens(params: any): void {
		this._activeDepartment = DepartmentType.SCREENS;
		this.init(params);
	}

	/** Spaces */
	public spaces(params: any): void {
		this._activeDepartment = DepartmentType.SPACES;
		this.init(params);
	}

	//Getters
	//-------------------------------------------------
	public isReadyToView(state: HistoryStateModel): boolean {
		//Need to check if HTML is loaded in background

		return true;
	}

	//Setters
	//---------------------------------------------------
	public setControllers(
		mouse: MouseController,
		scrollbar: ScrollbarController,
		verticalDrag: VerticalDragController,
		pixi: PixiController,
		screens: WorkController,
		spaces: WorkController,
		systems: WorkController
	): void {
		this._mouseController = mouse;
		this._scrollbarController = scrollbar;
		this._pixiController = pixi;
		this._screensController = screens;
		this._spacesController = spaces;
		this._systemsController = systems;
	}

	//Save a projects HTML to record:
	public setHTML(url: string, html: string): void {
		this._loading.store(url, html);

		//If the page is waiting for the HTML to load then
		if (this._loading.queued && this._loading.activeURL === url) {
			this._setProjectHTML(url);
			this._loading.queued = false;
		}
	}

	//---------------------------------------------------------------------
	//DOMReady:
	public DOMReady(): void {
		this._addListeners();
		if (this._active) {
			this._setRefs();
			this._createSidePaging();
		}
	}

	public resize(fromOrientationChange: boolean = false): void {
		console.log(
			'RESIZE',
			'fromOrientationChange',
			fromOrientationChange,
			'this._allowResize',
			this._allowResize,
			'this._isDeeplink ',
			this._isDeeplink
		);

		if (!this._isDeeplink && !this._allowResize && !fromOrientationChange) {
			return;
		}

		this._innerHeight = device.innerHeight;

		this._media.matchHeaderToPixi();

		if (this._active && globals.activeTile) {
			globals.activeTile.resizeOnProject();
			globals.activeWorkController.resizeOnProject();
		}
	}

	public freeze(): void {
		this._loading.queued = false;
		this._active = false;
		this._scrollbarController.reset();
		this._scrollbarController.setActive(false);
		this._timeouts.freeze = setTimeout(() => this._cleanupAfterFreeze(), 500);
		this._activeWorkController.freezeFromProjectView();
	}

	public setThemeType(type: string): void {
		this._setUiThemeType(type);
	}

	private _setUiThemeType(type: string) {
		if (type == 'dark') {
			(this.get('ui') as UIController).dark();
			this.DOM.addClass(document.querySelector('body'), '-darktheme');
		} else {
			(this.get('ui') as UIController).light();
			this.DOM.removeClass(document.querySelector('body'), '-darktheme');
		}
	}

	public setAllowScrollTweenUpdate(allow: boolean) {
		this._scroll.allowScrollTweenUpdate = allow;
	}

	public scroll(y: number): void {
		this._scroll.scroll(y);
	}

	public tick() {
		this._media.setHeaderVideoVisible();

		//Parallax on Work Tile / floating elemnts
		if (!this._isDeeplink && this._scroll.webGLActive && this._caseStudyActive) {
			(globals.activeTile as any).caseStudyTick(); //Zero-arg call of caseStudyTick(y) — pre-existing, preserved from the untyped global
		}
	}

	/** reset */
	public reset(oldState: HistoryStateModel = null, isDeeplink: boolean = false): void {
		let fromMenu = oldState && (oldState.section == SectionType.ABOUT || oldState.section == SectionType.PROJECT);

		console.log('window.scroll 0,0');
		window.scroll(0, 0);

		if (fromMenu) {
			return;
		}

		this._loading.queued = false;
		this._oldState = oldState;
		this._isDeeplink = isDeeplink;

		if (this._isDeeplink) {
			this.DOM.addClass(this.container, '-deeplink');
		}

		this.DOM.addClass(this.container, '-closevisible');
		this.DOM.addClass(this.container, '-projectactive');

		this._scroll.closeButtonVisible = true;

		this._scroll.bottomScrollReached = false;
		this.DOM.removeClass(this.container, '-pagebottom');
		this.DOM.removeClass(this.container, '-backclicked');
		this.DOM.removeClass(this.container, '-scrollingtohideproject');
		this.DOM.removeClass(document.querySelector('body'), '-footerclosebuttonclicked');

		this.DOM.removeClass(this.container, '-scrollpastheader');
		(this.get('ui') as UIController).light();
		this.DOM.removeClass(document.querySelector('body'), '-darktheme');
		this._scroll.lowerUIThemeActive = false;

		gsap.TweenMax.set(this._view.$dynamicContent.querySelectorAll('.content-row'), { opacity: 1 });

		if (this._activeProjectIsSetup()) {
			this._reset();
		}
	}

	/** Pause when menu is shown */

	/** Pause when menu is hidden */

	/**
	 * Reset to zero
	 * This will trigger if
	 */

	/** Show the view */
	public show(oldState: HistoryStateModel = null, isDeeplink: boolean = false): void {
		if (!this._$circleProgess) {
			this._$circleProgess = document.querySelector('.circular-progress.-project');
			SiteTransitions.$circleCloseProgess = this._$circleProgess;
		}
		console.log('window.scroll 0,0');
		window.scroll(0, 0);
		gsap.TweenMax.set(this._view.$dynamicContent, { y: 0 });

		if (oldState && oldState.section === SectionType.ABOUT) {
			this._innerHeight = device.innerHeight;
			this._active = true;
			return;
		}

		this._innerHeight = device.innerHeight;

		if (this._activeProjectIsSetup()) {
			this._show();
			return;
		}

		//HTML is not loaded so can't be setup:
		this._isSetup = false;

		//Need to load the actual project HTML:
		if (!this._active) {
			this._setProjectHTML(this._state.href);
		} else {
			this._setup();
			this._reset();
			this._show();
		}

		DelayHelper.setTimeout(() => {
			this._active = true;
		}, 800);
	}

	/** Hide */
	public hide(newState: HistoryStateModel = null): void {
		if (newState.section === SectionType.ABOUT) {
			return;
		}

		this._closingProject = true;

		//After the project is 100% hidden then can do all resets + hides etc:
		SiteTransitions.projectIsHiddenCallback = () => {
			this._resetCore();
		};

		SiteTransitions.startProjectToWork(this._scroll.headerHeight, this._activeWorkController, {
			headerVideo: this._media.$headerVideo,
			headerVideoOverlay: this._media.$headerVideoOverlay,
			loader: this._view.$loading
		});

		this._hideHeaderVideo();

		this.DOM.removeClass(document.querySelector('body'), '-darktheme');
		this.DOM.removeClass(this.container.querySelector('.project__center__close'), '-show');
		this.DOM.removeClass(this.container, '-closevisible');
		this.DOM.removeClass(this.container, '-projectactive');
		this.DOM.removeClass(this.container, '-pagebottom');
		this.DOM.removeClass(this.container, '-scrollpastheader');

		this._scroll.closeButtonVisible = false;
		this._scroll.bottomScrollReached = false;
		this._active = false;
		this._caseStudyActive = false;
	}

	private _resetCore() {
		console.log('RESET CORE----------------');

		console.log('window.scroll 0,0');
		window.scroll(0, 0);

		this._active = false;
		this._scroll.scrollProgress = 0;
		this._closingProject = false;
		this._scroll.bottomScrollReached = false;
		this.DOM.removeClass(this.container, '-active');
		this.DOM.removeClass(this.container, '-uivisible');
		this.DOM.removeClass(this.container, '-footerclosebuttonclicked');
		this.DOM.removeClass(document.querySelector('body'), '-projectpagebottom');

		if (this._scrollbarController.scrollerType === ScrollerType.NATIVE) {
			(this.container as HTMLElement).style.display = 'none';
		}
	}

	/** Set active department / slug */
	public setState(state: HistoryStateModel): void {
		this._state = state;
		this._activeDepartment = state.subsection as DepartmentType;
		this._activeSlug = state.projectSlug;

		if (this._activeDepartment === DepartmentType.SCREENS) this._activeWorkController = this._screensController;
		else if (this._activeDepartment === DepartmentType.SPACES) this._activeWorkController = this._spacesController;
		else if (this._activeDepartment === DepartmentType.SYSTEMS) this._activeWorkController = this._systemsController;
	}

	//Go back to work on back press:
	public historyStateBackToWork(): void {
		this._onCloseClick(null, false);
	}

	public matchContentYPosition() {
		this._scroll.matchContentYPosition();
	}

	public resetScroll() {}

	//Private methods:
	//-------------------------------------------------------------------------------------
	//-------------------------------------------------------------------------------------
	//-------------------------------------------------------------------------------------
	//-------------------------------------------------------------------------------------

	private _activeProjectIsSetup(): boolean {
		return this._state.href === this._loading.activeURL && this._isSetup;
	}

	/** DOM elements for project should be aviable so  */
	private _setup(): void {
		this._isSetup = true;
	}

	private _setRefs(): void {
		this._view.queryRefs(this.container);
		this._media.queryRefs(this.container);

		SiteTransitions.$projectContainer = this.container;
		SiteTransitions.$projectContent = this._view.$content;
		SiteTransitions.$projectDynamicContent = this._view.$dynamicContent;
	}

	private _createSidePaging(): void {
		this._view.createSidePaging(this.container);

		Events.on(SidePagingClickEvent, (event: SidePagingClickEvent) => {
			this._scrollbarController.scrollToSelector(event.targetSelector);
		});

		Events.on(SidebarChangeEvent, (event: SidebarChangeEvent) => {
			this._view.selectSidePagingIndex(event.index, event.theme);
		});
	}

	private _addListeners(): void {
		Events.on('click', '.ui_close.project__close__button', this._onCloseClick.bind(this));
		Events.on('click', '.project-close-progress-button', (e) => {
			e.preventDefault();
			this._onCloseClick();
			Simple.event('close_project_top_button_click', { slug: this._activeSlug });
		});
		Events.on('click', '.back-to-project-trigger', this._onBottomCloseClick.bind(this));
		let $close = document.querySelector('.project__center__close');
		if (!$close || !this.DOM.hasClass($close, '-deeplink')) {
			Events.on('click', '.project__center__close', this._onCloseClick.bind(this));
		}

		Events.on(AppEvents.EXIT_PROJECT_PRESS, this._onCloseClick.bind(this));
		Events.on(QueProjectEvent, this._onQueProject.bind(this));

		//WHen orientation size event fires then can resize the header
		window.addEventListener('orientationchange', () => {
			this._media.headerSized = false;

			if (!this._allowResize) {
				var afterOrientationChange = () => {
					device.resize();
					this.get('pixi').resize(true);
					this._activeWorkController.resize(true);
					this.resize(true);
					// Remove the resize event listener after it has executed
					window.removeEventListener('resize', afterOrientationChange);
				};
				window.addEventListener('resize', afterOrientationChange);
			}
		});
	}

	/** Set HTML for project */
	private _setProjectHTML(url: string): void {
		this._loading.activeURL = url;
		let html = this._loading.getHTML(this._loading.activeURL);

		//If no is no HTML then que
		if (!html) {
			console.log('_noHTML');
			this._loading.queued = true;
			this.DOM.addClass(this.container, '-active');
			return;
		}

		this.DOM.setHTML(this._view.$dynamicContent, html, () => {
			this.module.destroyByTag('project');
			(window as any).observer.observe();
			this._setup();
			this._reset();
			this._show();
		});
	}

	/** Que project up as it's focus on work timeline */
	private _onQueProject(event: QueProjectEvent): void {
		//If slug is null the exit (As its firts spacer item)
		if (event.slug === null) {
			return;
		}

		this._media.headerType = event.headerType || HeaderType.IMAGE;
		this._topUITheme = event.topUITheme || 'light';
		//If DOM contaner for project is not setup yet then add and call the function again:

		if (!this._DOMStructureSet) {
			this.DOM.append(document.querySelector('body'), this._view.createDOMStructure(this._media.headerType), () => {
				this.container = document.querySelector('.project');
				this._setRefs();
				this._createSidePaging();
				this._DOMStructureSet = true;
				this._onQueProject(event);
			});
			return;
		}

		this._media.queProjectMedia(event.slug);
	}

	//Reset functions
	//-------------------------------------------------------------------

	private _reset(): void {
		//Generic reset:
		this._closingProject = false;
		this._caseStudyActive = false;

		//Set the site bar items;
		this._view.setSidePagingItems(this.container);

		//Reste scale / Reset scroll of container:
		gsap.TweenMax.set([this.container], { scale: 1 });

		//Reset transition out factors after scroll passes header:
		this.DOM.removeClass(this._view.$content, '-maskedexit');
		this._view.$dynamicContent.style.top = '0px';
		this._media.$headerVideoOverlay.style.opacity = '0';

		//Resize to ensure elements are at matching scale / sizes
		this._media.headerSized = false;
		this.resize();

		//Is a deeplink for get ready to show coming direct from Preoader
		if (this._isDeeplink) {
			this._resetForDeeplink();
			return;
		}

		if (this._oldState.section === SectionType.WORK) {
			this._resetFromWork(this._oldState.subsection as DepartmentType);
		}
	}

	/** Reset from fullscreen loader */
	private _resetForDeeplink(): void {}

	/** Reset from Work section */
	private _resetFromWork(department: DepartmentType): void {
		this._media.headerSized = false;
	}

	/** After Freeze hidden to anything that can't be done while showing */
	private _cleanupAfterFreeze(): void {
		this.DOM.removeClass(this.container, '-active');
		this.DOM.removeClass(this._view.$home, '-active');
		this.DOM.removeClass(this._view.$home, '-show');
		gsap.TweenMax.set([this.container], { scale: 1 });
		this._closingProject = false;
		this._media.pauseHeaderVideo();
	}

	//Show functions
	//--------------------------------------------------------------------

	private _show(): void {
		//Generic show actions:
		this.DOM.addClass(this.container, '-active');

		this._active = true;

		this._scroll.projectPageBottomTracked = false;
		this._scroll.projectPageMiddleTracked = false;

		this._scroll.lowerUIThemeActive = false;
		this._setUiThemeType(this._topUITheme);

		this._pixiController.setAllowSlowFrameCheck(false);
		this._scroll.bottomScrollReached = false;
		this._mouseController.setColour('dark');
		this._scroll.webGLActive = true;

		if (this._scrollbarController.shouldResizeLock()) {
			this._allowResize = false;
			this.get('pixi').setAllowResize(false);
		}

		this._mouseController.setDragCursorActive(false);
		this._mouseController.setHiddenState(true);

		let footer = document.querySelector('.project__footer');
		let bottomCloseButton = document.querySelector('.project-close-bottom-container');

		Events.on('mouseover', bottomCloseButton, () => {
			this.DOM.addClass(footer, '-buttonhover');
		});

		Events.on('mouseout', bottomCloseButton, () => {
			this.DOM.removeClass(footer, '-buttonhover');
		});

		if (this._isDeeplink) {
			this._showForDeeplink();
			return;
		}

		if (this._oldState.section === SectionType.WORK) {
			this._showFromWork(this._oldState.subsection as DepartmentType);
		}

		//Make sure section is shown
		if (this._scrollbarController.scrollerType === ScrollerType.NATIVE) {
			(this.container as HTMLElement).style.display = 'block';
		}

		if (!this._scrollbarController.pixiScrollActive) {
			DelayHelper.setTimeout(() => {
				if (!this._active || this._closingProject) {
					return;
				}

				this._pixiController.pause();
				this._scroll.webGLActive = false;
			}, 1200);
		}
	}


	/** Show from fullscreen preloader */
	private _showForDeeplink(): void {
		this._media.$headerTarget = this._media.$headerImage;
		(window as any).observer.observe();

		this.DOM.addClass(this._view.$home, '-active');
		this.DOM.addClass(this._view.$home, '-show');

		gsap.TweenMax.set(this._view.$close, { autoAlpha: 0 });
		//3.) Show the paging
		this._view.showSidePaging();

		this.resize();
		this._showHeaderVideo(0);

		SiteTransitions.showProjectHeaderVideo(this._media.$headerVideo, 0, () => {
			this._media.headerVideoPlaying = true;
		});

		SiteTransitions.showCloseButton(1000);
		SiteTransitions.activateScrollbar(1000, true, this._view.$content);
	}

	/** Animate in the project */
	private _showFromWork(department: DepartmentType): void {
		this._showHeaderVideo(0);
		this._caseStudyActive = true;
	}

	public getDynamicContentRef(): HTMLElement {
		return this._view.$dynamicContent;
	}

	/** Show and play the fullscreen header video */
	private _showHeaderVideo(delay: number = 0): void {
		this._media.headerSized = false;

		if (this._media.headerType === HeaderType.IMAGE) {
			return;
		}

		this._media.playHeaderVideoFromStart();

		SiteTransitions.showProjectHeaderVideo(this._media.$headerVideo, delay + 1, () => {
			this._media.headerVideoPlaying = true;
		});
	}

	/** Pause and hide the header video when transiting back to project */
	private _hideHeaderVideo(delay: number = 0): void {
		this._media.pauseHeaderVideoIfVideo();

		SiteTransitions.hideProjectHeaderVideo(this._media.$headerVideo, delay);
	}

	//----------------------------------------------------------------------------

	private _onBottomCloseClick(event: MouseEvent = null, processHistoryState: boolean = true) {
		if (event) {
			event.preventDefault();
		}

		this.get('Mouse').removeLock();

		this.DOM.addClass(this.container, '-backclicked');
		this.DOM.addClass(document.querySelector('body'), '-footerclosebuttonclicked');
		this._onCloseClick();

		Simple.event('close_project_lower_button_click', { slug: this._activeSlug });
	}

	/**
	 * Close button click
	 * @param {MouseEvent} event
	 */
	private _onCloseClick(event: MouseEvent = null, processHistoryState: boolean = true): boolean {
		if (event) {
			event.preventDefault();
		}

		if (this._isDeeplink) {
			window.location.href = '/';
			return;
		}

		if (this._closingProject) {
			return;
		}

		//Setup section change event -------------------------------------------

		let title = '';

		if (this._activeDepartment === DepartmentType.SCREENS) title = 'Screens';
		else if (this._activeDepartment === DepartmentType.SPACES) 'Spaces';
		else title = 'Systems';

		let url = `/${this._activeDepartment}`;

		let newState: HistoryStateModel = {
			section: SectionType.WORK,
			subsection: this._activeDepartment,
			title: title,
			href: url,
			skipHistoryState: !processHistoryState
		};

		//Flag close to stop repeating -------------------------------
		this._closingProject = true;

		Events.dispatch(AppEvents.CHANGE_SECTION, newState);

		return false;
	}

	private _onThemeChange(event: UIThemeChangeEvent): void {
		if (!this._scroll.lowerUIThemeActive) {
			return;
		}

		this._setUiThemeType(event.theme);
	}
}
